import { Property } from '../../services/api';
import { colors } from '../../design-system/tokens/colors';

/** 재오픈예정(즉시입주 잔여세대) 마커 색 — 하늘빛 파랑, 임대 파랑(info)과 구분 (2026-09-28 대표) */
const REOPEN_MARKER_COLORS = { bg: colors.reopen, text: colors.inverseFg };
export const UPCOMING_YELLOW = '#FACC15';   // 분양예정 마커(일반·추천 배너 공통) — MapPage.css .is-upcoming 과 같은 값

// 일반(고양이귀) 마커 색 — v2 (2026-09-21 배치 4, 계획서 3장 E / 대표 결정 #5):
//   임대·분양임대 = 파랑 #2563EB(info), 그 외(혜택 없음) = 잉크 #0F172A.
//   분양예정 초록·상태별 오렌지/회색은 제거 — 상태는 라벨 텍스트로만 구분한다.
//   혜택 있는 현장은 배너(오렌지) 마커라 이 함수를 쓰지 않는다.
// SVG 마커 렌더라 CSS 클래스 대신 hex 필요 → tokens/colors.ts import.
export const getStatusColor = (status: string) => {
  // 완판 — 빨강으로 눈에 띄게 (2026-09-26 대표: "빨리 완판됐구나" 긴박감)
  if (status === '분양완료') {
    return { bg: colors.bad, text: colors.inverseFg };
  }
  if (status === '임대' || status === '분양임대') {
    return { bg: colors.info, text: colors.inverseFg };
  }
  // 분양예정 — 크림냥이 노랑 + 검은 글씨(2026-10-06 대표: "분양예정 현장은 마커 색상을 다르게", "눈에 잘 띄는 걸로").
  //   흰냥이·고등어(회색)는 밝은 지도 바탕에 묻혀 제외, 치즈냥이(주황)는 추천·혜택 배너가 이미 사용.
  if (status === '분양예정') {
    return { bg: UPCOMING_YELLOW, text: colors.ink };
  }
  return { bg: colors.ink, text: colors.inverseFg };
};

// 고양이 귀 마커 SVG 생성 (원본 로고 이미지 정확히 반영 - 귀 2개)
export const createCatEarMarkerSVG = (status: string, label: string) => {
  // 재오픈예정은 필터 상태가 분양중이라 라벨로 색을 구분한다
  const colors = label === '재오픈예정' ? REOPEN_MARKER_COLORS : getStatusColor(status);

  // 4글자 라벨("분양임대"·"분양완료")은 두 줄로 — 앞 두 글자 / 뒤 두 글자.
  // '재오픈예정'(2026-09-28 즉시입주 잔여세대)은 '재오픈' / '예정'.
  const isReopen = label === '재오픈예정';
  const isTwoLine = label === '분양임대' || label === '분양완료' || label === '분양예정' || isReopen;
  const line1 = isReopen ? '재오픈' : label.slice(0, 2);
  const line2 = isReopen ? '예정' : label.slice(2, 4);
  const shortLabel = label.length > 4 ? label.substring(0, 4) : label;

  // 2줄 텍스트 (분양임대·분양완료)
  const twoLineText = `
      <text
        x="50"
        y="48"
        text-anchor="middle"
        dominant-baseline="middle"
        fill="${colors.text}"
        font-size="17"
        font-weight="700"
        font-family="sans-serif"
      >${line1}</text>
      <text
        x="50"
        y="68"
        text-anchor="middle"
        dominant-baseline="middle"
        fill="${colors.text}"
        font-size="17"
        font-weight="700"
        font-family="sans-serif"
      >${line2}</text>
  `;

  // 1줄 텍스트 (일반)
  const oneLineText = `
      <text
        x="50"
        y="58"
        text-anchor="middle"
        dominant-baseline="middle"
        fill="${colors.text}"
        font-size="21"
        font-weight="700"
        font-family="sans-serif"
      >${shortLabel}</text>
  `;

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="52" height="56" viewBox="0 0 100 108">
      <path
        d="M8 28
           L8 8
           L32 32
           L68 32
           L92 8
           L92 28
           L92 78
           Q92 88 82 88
           L72 88
           L62 102
           L52 88
           L18 88
           Q8 88 8 78
           Z"
        fill="${colors.bg}"
        stroke="#ffffff"
        stroke-width="3"
      />
      ${isTwoLine ? twoLineText : oneLineText}
    </svg>
  `;
};

// 텍스트 길이에 따른 폰트 크기 계산 (최소 10px — 마커 한 줄에 들어가도록)
export const getAdaptiveFontSize = (text: string, baseFontSize: number, maxChars: number): number => {
  const length = (text || '').length;
  if (length <= maxChars) return baseFontSize;
  return Math.max(baseFontSize - Math.ceil((length - maxChars) / 2), 10);
};

// innerHTML 삽입 전 이스케이프 (관리자 입력값 유래 텍스트 — XSS 방지)
export const escapeHtml = (text: string): string =>
  (text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// 배너(특별) 마커 사용 여부 — 대표 지시 2026-09-21:
// 활성·미만료 혜택이 1건 이상인 현장만 배너, 나머지는 일반(고양이귀) 마커.
// is_special 은 메인노출(is_main_display)과 통일된 값이라 마커 분기에 쓰지 않는다.
// has_active_benefit 은 map API 에서만 내려온다(없으면 일반 마커).
// 2026-10-06 대표: "특별현장 (마커크게), 우리 담당자있는 현장은 특별현장이다" — 추천(특별) 현장은 금액 할인이 없어도 배너(큰 마커).
//   배너 안 노란 칩 문구(banner_text)는 10/05 결정대로 금액 할인 현장만 — 없으면 단지명만 든 배너.
export const usesBannerMarker = (property: Property): boolean =>
  property.has_active_benefit === true || property.is_special === true;

// 배너에 표시하면 안 되는 상태 단어 (대표 지시 2026-09-21: 배너에는 상태 문구 절대 금지)
const STATUS_WORDS = new Set(['분양중', '분양예정', '재오픈예정', '입주예정', '마감임박', '분양완료', '입주완료', '계약완료']);

// 수동 문구가 상태 단어 그 자체면 화면에서만 건너뛴다 (DB 값은 수정하지 않음)
const benefitTextOrEmpty = (text?: string | null): string => {
  const t = (text || '').trim();
  return t && !STATUS_WORDS.has(t.replace(/\s+/g, '')) ? t : '';
};

// 특별현장 마커 HTML 생성 (빨간 배너 스타일)
export const createSpecialMarkerHTML = (property: Property) => {
  // 1·2번째 줄은 절대로 단지명을 노출하지 않는다 (사용자 지시 2026-06-04).
  // 문구 결정은 서버 단일 소스(map API banner_text/banner_subtext = services/benefits.build_banner_text):
  //   대표 지시 2026-09-22 — 할인율 → 할인금액 → 가격(원분양가/할인가 하한) → 수동 문구 → 대표 혜택 제목.
  //   서브 = 가격 보조(타 주택형 할인율·할인금액) → 수동 서브 → 비움.
  // 상태 문구(분양중 등)는 여기서도 한 번 더 걸러낸다 (대표 지시 2026-09-21).
  // banner_text 필드가 없는 응답(구 백엔드)만 이전 규칙(수동 → 대표 혜택)으로 폴백.
  const fromServer = property.banner_text !== undefined || property.banner_subtext !== undefined;
  const mainText = fromServer
    ? benefitTextOrEmpty(property.banner_text)
    : benefitTextOrEmpty(property.special_marker_text) || benefitTextOrEmpty(property.benefit_headline?.text);
  const subText = fromServer
    ? benefitTextOrEmpty(property.banner_subtext)
    : benefitTextOrEmpty(property.special_marker_subtext) || benefitTextOrEmpty(property.benefit_headline?.sub);
  // 맨 아래 title 에서 "xx동 " prefix 를 제거해 아파트명이 잘리지 않게 한다
  // (예: "황금동 힐스테이트 황금역 리저브 1단지" → "힐스테이트 황금역 리저브 1단지").
  const titleText = (property.title || '').replace(/^[가-힣]{1,5}동\s+/, '');

  // 텍스트 길이에 따른 동적 폰트 크기 (최소 10px). max-width 144px CSS 와 함께 동작.
  const mainFontSize = getAdaptiveFontSize(mainText, 13, 7);
  const subFontSize = getAdaptiveFontSize(subText, 13, 8);
  const titleFontSize = getAdaptiveFontSize(titleText, 12, 11);

  // v2 마크업 (목업 `.marker > .bub(em 노란 칩 + b 단지명) + .tip`). 클래스명은 기존 유지(테스트·드래그 숨김 CSS 호환).
  return `
    <div class="special-marker-container v2-marker${property.status === '분양예정' ? ' is-upcoming' : ''}">
      <div class="special-marker-banner">
        ${mainText ? `<em class="special-marker-main-text" style="font-size: ${Math.min(mainFontSize, 11)}px">${escapeHtml(mainText)}</em>` : ''}
        ${subText ? `<span class="special-marker-sub-text" style="font-size: ${Math.min(subFontSize, 10)}px">${escapeHtml(subText)}</span>` : ''}
        <b class="special-marker-title" style="font-size: ${titleFontSize}px">${escapeHtml(titleText)}</b>
      </div>
      <div class="special-marker-pointer"></div>
    </div>
  `;
};
